import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { Polyline, useMap } from 'react-leaflet';
import L, { LatLngBounds } from 'leaflet';
import { AppContext } from '../../../context/context';
import { isTripLineShape, getResolvedMissionControlLine } from '../functions/missionControlTripStyles';
import type { Line } from '../../../context/context';

/**
 * Maximum number of trip lines rendered on the canvas by default.
 * All trips remain in state and are always sent to simulations.
 * The user can bypass this cap per-layer via layer.renderAll.
 */
export const TRIP_RENDER_CAP = 150;

/**
 * Renders all imported trip lines using Leaflet's canvas renderer.
 *
 * This component must live inside a react-leaflet MapContainer so it can access
 * the map instance via useMap(). It handles three performance concerns:
 *  1. Canvas rendering   - all trip lines share one <canvas> element instead of N SVG paths
 *  2. Viewport culling   - only lines whose bounding box intersects the current view are mounted
 *  3. Render cap         - at most TRIP_RENDER_CAP lines are drawn unless layer.renderAll is set
 *
 * Non-trip lines (fixed routes, hand-drawn) are intentionally excluded and remain
 * in useRenderLines() with SVG rendering and full edit-mode support.
 */
const TripLayerRenderer: React.FC = () => {
    const context = useContext(AppContext);
    const map = useMap();

    // One canvas renderer per map instance — shared by all trip lines in this map.
    const canvasRenderer = useRef(L.canvas({ padding: 0.5 }));

    const [viewportBounds, setViewportBounds] = React.useState<LatLngBounds>(
        () => map.getBounds()
    );

    useEffect(() => {
        const onViewportChange = () => setViewportBounds(map.getBounds());
        map.on('moveend', onViewportChange);
        map.on('zoomend', onViewportChange);
        return () => {
            map.off('moveend', onViewportChange);
            map.off('zoomend', onViewportChange);
        };
    }, [map]);

    // Use refs for mode and setSelectedShapes so event handlers stay stable
    // across renders without forcing a full element recomputation.
    const modeRef = useRef(context?.modeSettings.mode ?? '');
    const setSelectedShapesRef = useRef(context?.global.setSelectedShapes);

    useEffect(() => {
        modeRef.current = context?.modeSettings.mode ?? '';
    }, [context?.modeSettings.mode]);

    useEffect(() => {
        setSelectedShapesRef.current = context?.global.setSelectedShapes;
    }, [context?.global.setSelectedShapes]);

    const mapLayers = context?.global.mapLayers ?? [];
    const selectedShapes = context?.global.selectedShapes ?? [];

    const elements = useMemo(() => {
        const result: React.ReactElement[] = [];

        for (const layer of [...mapLayers].reverse()) {
            if (layer.hidden) continue;

            const tripLines = layer.shapes.filter(
                s => !s.hidden && isTripLineShape(s)
            ) as Line[];

            if (tripLines.length === 0) continue;

            const shouldCap = tripLines.length > TRIP_RENDER_CAP && !layer.renderAll;
            const linesToRender = shouldCap ? tripLines.slice(0, TRIP_RENDER_CAP) : tripLines;

            for (const line of linesToRender) {
                const isSelected = selectedShapes.includes(line.id);

                // Prefer positions derived from linked origin/dest point shapes when available,
                // since points may have been moved interactively after import.
                let positions: [number, number][] = line.coordinates
                    .filter(c =>
                        Array.isArray(c) &&
                        c.length >= 2 &&
                        typeof c[0] === 'number' &&
                        typeof c[1] === 'number' &&
                        isFinite(c[0]) &&
                        isFinite(c[1])
                    )
                    .map(c => [c[0], c[1]] as [number, number]);

                const linkedLine = line as Line & Partial<{ originPointId: string; destPointId: string }>;
                if (linkedLine.originPointId && linkedLine.destPointId) {
                    const origin = layer.shapes.find(
                        s => s.id === linkedLine.originPointId && s.type === 'point'
                    );
                    const dest = layer.shapes.find(
                        s => s.id === linkedLine.destPointId && s.type === 'point'
                    );
                    if (origin && dest && origin.type === 'point' && dest.type === 'point') {
                        positions = [
                            [origin.coordinates[0], origin.coordinates[1]],
                            [dest.coordinates[0], dest.coordinates[1]],
                        ];
                    }
                }

                if (positions.length < 2) continue;

                // Viewport cull — selected lines are always visible regardless of position.
                if (!isSelected) {
                    try {
                        const shapeBounds = L.latLngBounds(
                            positions.map(p => L.latLng(p[0], p[1]))
                        );
                        if (!viewportBounds.intersects(shapeBounds.pad(0.1))) continue;
                    } catch {
                        continue;
                    }
                }

                const resolved = getResolvedMissionControlLine(line, true);

                result.push(
                    <Polyline
                        key={line.id}
                        positions={positions}
                        pathOptions={{
                            color: resolved.borderColor,
                            weight: isSelected ? resolved.width + 2 : resolved.width,
                            opacity: isSelected ? 1 : resolved.opacity,
                            renderer: canvasRenderer.current,
                            dashArray:
                                !line.dashPattern || line.dashPattern === 'none'
                                    ? undefined
                                    : line.dashPattern,
                        }}
                        eventHandlers={{
                            click: (e) => {
                                if (modeRef.current === 'Select') {
                                    e.originalEvent.preventDefault();
                                    const setter = setSelectedShapesRef.current;
                                    if (!setter) return;
                                    if (e.originalEvent.ctrlKey) {
                                        setter((prev: string[]) =>
                                            prev.includes(line.id)
                                                ? prev.filter(id => id !== line.id)
                                                : [...prev, line.id]
                                        );
                                    } else {
                                        setter([line.id]);
                                    }
                                }
                            },
                            contextmenu: (e) => {
                                e.originalEvent.preventDefault();
                                window.dispatchEvent(
                                    new CustomEvent('lineContextMenu', {
                                        detail: {
                                            x: e.originalEvent.clientX,
                                            y: e.originalEvent.clientY,
                                            line,
                                        },
                                    })
                                );
                            },
                        }}
                    />
                );
            }
        }

        return result;
        // viewportBounds drives culling; mapLayers + selectedShapes drive shape content.
        // modeRef/setSelectedShapesRef are refs so they don't need to be deps.
    }, [mapLayers, selectedShapes, viewportBounds]);

    return <>{elements}</>;
};

export default TripLayerRenderer;
